import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, Button, Badge } from '../components/Common';

const STATUS_COLORS = {
  Requested: '#6366f1', New: '#3b82f6', Scoping: '#8b5cf6', 'Contract Sent': '#06b6d4',
  'In Progress': '#f59e0b', 'In Review': '#f97316', Complete: '#22c55e', 'On Hold': '#6b7280',
  'In Retest': '#14b8a6', 'Retest Complete': '#10b981',
};

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];

function daysInMonth(year, month) { return new Date(year, month + 1, 0).getDate(); }
function formatDate(y, m, d) { return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`; }

export default function Calendar() {
  const { apiFetch, user, hasGroup } = useAuth();
  const navigate = useNavigate();
  const isMsp = user?.isMsp || hasGroup('Super Admins');

  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth());
  const [engagements, setEngagements] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientFilter, setClientFilter] = useState('');
  const [testerFilter, setTesterFilter] = useState('');

  const numDays = daysInMonth(year, month);
  const startDate = formatDate(year, month, 1);
  const endDate = formatDate(year, month, numDays);

  const loadData = useCallback(async () => {
    try {
      const params = new URLSearchParams({ start: startDate, end: endDate });
      if (clientFilter) params.set('clientId', clientFilter);
      if (testerFilter) params.set('testerId', testerFilter);
      const res = await apiFetch(`/engagements/calendar?${params}`);
      if (res.ok) setEngagements(await res.json());
    } catch {}
  }, [apiFetch, startDate, endDate, clientFilter, testerFilter]);

  const loadClients = useCallback(async () => {
    if (!isMsp) return;
    try {
      const res = await apiFetch('/clients');
      if (res.ok) setClients(await res.json());
    } catch {}
  }, [apiFetch, isMsp]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { loadClients(); }, [loadClients]);

  const prevMonth = () => { if (month === 0) { setMonth(11); setYear(y => y - 1); } else setMonth(m => m - 1); };
  const nextMonth = () => { if (month === 11) { setMonth(0); setYear(y => y + 1); } else setMonth(m => m + 1); };
  const goToday = () => { setYear(new Date().getFullYear()); setMonth(new Date().getMonth()); };

  // Group engagements by tester
  const testerMap = new Map();
  const unassigned = [];
  engagements.forEach(e => {
    if (!e.tester_id) { unassigned.push(e); return; }
    if (!testerMap.has(e.tester_id)) testerMap.set(e.tester_id, { name: e.tester_name || 'Unknown', engagements: [] });
    testerMap.get(e.tester_id).engagements.push(e);
  });

  const testers = [...testerMap.entries()].sort((a, b) => a[1].name.localeCompare(b[1].name));
  const uniqueTesters = [...new Map(engagements.filter(e => e.tester_id).map(e => [e.tester_id, e.tester_name])).entries()];

  const today = new Date();
  const todayStr = formatDate(today.getFullYear(), today.getMonth(), today.getDate());

  const selectStyle = {
    padding: '6px 10px', borderRadius: 'var(--radius)', border: '1px solid var(--border)',
    background: 'var(--bg-input)', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit',
  };

  const dayWidth = 100 / numDays;

  function renderBar(eng) {
    const eStart = eng.start_date ? new Date(eng.start_date) : null;
    const eEnd = eng.end_date ? new Date(eng.end_date) : null;
    if (!eStart || !eEnd) return null;

    const monthStart = new Date(year, month, 1);
    const monthEnd = new Date(year, month, numDays);
    const barStart = eStart < monthStart ? monthStart : eStart;
    const barEnd = eEnd > monthEnd ? monthEnd : eEnd;

    const startDay = barStart.getDate();
    const endDay = barEnd.getDate();
    const left = ((startDay - 1) / numDays) * 100;
    const width = ((endDay - startDay + 1) / numDays) * 100;
    const color = STATUS_COLORS[eng.status] || '#6b7280';

    return (
      <div key={eng.id}
        onClick={() => navigate(`/engagements/${eng.id}`)}
        title={`${eng.name || eng.engagement_type}\n${eng.client_name}\n${eng.status}\n${eng.start_date?.split('T')[0]} → ${eng.end_date?.split('T')[0]}`}
        style={{
          position: 'absolute', top: 2, height: 24, borderRadius: 4,
          left: `${left}%`, width: `${Math.max(width, dayWidth)}%`,
          background: color, color: '#fff', fontSize: 10, fontWeight: 600,
          padding: '0 6px', display: 'flex', alignItems: 'center',
          overflow: 'hidden', whiteSpace: 'nowrap', cursor: 'pointer',
          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        }}>
        {eng.client_name ? `${eng.client_name} — ` : ''}{eng.name || eng.engagement_type}
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700 }}>Resource Calendar</h1>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Button variant="secondary" onClick={prevMonth} style={{ padding: '6px 12px' }}>◀</Button>
          <span style={{ fontSize: 16, fontWeight: 600, minWidth: 160, textAlign: 'center' }}>
            {MONTH_NAMES[month]} {year}
          </span>
          <Button variant="secondary" onClick={nextMonth} style={{ padding: '6px 12px' }}>▶</Button>
          <Button variant="ghost" onClick={goToday} style={{ padding: '6px 14px', fontSize: 12 }}>Today</Button>
        </div>
      </div>

      {/* Filters */}
      <Card style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', padding: '8px 16px' }}>
        {isMsp && (
          <select value={clientFilter} onChange={e => setClientFilter(e.target.value)} style={selectStyle}>
            <option value="">All Clients</option>
            {clients.filter(c => c.is_active).sort((a, b) => a.name.localeCompare(b.name)).map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        )}
        <select value={testerFilter} onChange={e => setTesterFilter(e.target.value)} style={selectStyle}>
          <option value="">All Testers</option>
          {uniqueTesters.sort((a, b) => (a[1] || '').localeCompare(b[1] || '')).map(([id, name]) => (
            <option key={id} value={id}>{name}</option>
          ))}
        </select>
        <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
          {engagements.length} engagement{engagements.length !== 1 ? 's' : ''} this month
        </span>
      </Card>

      {/* Calendar Grid */}
      <Card style={{ overflowX: 'auto' }}>
        <div style={{ minWidth: 900 }}>
          {/* Day headers */}
          <div style={{ display: 'flex', borderBottom: '1px solid var(--border)' }}>
            <div style={{ width: 140, flexShrink: 0, padding: '8px 12px', fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)' }}>TESTER</div>
            {Array.from({ length: numDays }, (_, i) => {
              const d = new Date(year, month, i + 1);
              const isWeekend = d.getDay() === 0 || d.getDay() === 6;
              const isToday = formatDate(year, month, i + 1) === todayStr;
              return (
                <div key={i} style={{
                  flex: 1, textAlign: 'center', padding: '6px 0', fontSize: 10, fontWeight: isToday ? 700 : 400,
                  color: isToday ? 'var(--primary)' : isWeekend ? 'var(--text-muted)' : 'var(--text-secondary)',
                  background: isToday ? 'rgba(59,130,246,0.06)' : isWeekend ? 'rgba(100,116,139,0.03)' : 'transparent',
                  borderBottom: isToday ? '2px solid var(--primary)' : 'none',
                }}>
                  {i + 1}
                </div>
              );
            })}
          </div>

          {/* Tester rows */}
          {testers.map(([testerId, testerData]) => (
            <div key={testerId} style={{ display: 'flex', borderBottom: '1px solid var(--border)', minHeight: 32 }}>
              <div style={{ width: 140, flexShrink: 0, padding: '6px 12px', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                {testerData.name}
              </div>
              <div style={{ flex: 1, position: 'relative', minHeight: 28 }}>
                {testerData.engagements.map(eng => renderBar(eng))}
              </div>
            </div>
          ))}

          {/* Unassigned */}
          {unassigned.length > 0 && (
            <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', minHeight: 32 }}>
              <div style={{ width: 140, flexShrink: 0, padding: '6px 12px', fontSize: 12, fontWeight: 600, color: '#f59e0b', display: 'flex', alignItems: 'center' }}>
                Unassigned ({unassigned.length})
              </div>
              <div style={{ flex: 1, position: 'relative', minHeight: 28 }}>
                {unassigned.filter(e => e.start_date && e.end_date).map(eng => renderBar(eng))}
              </div>
            </div>
          )}

          {engagements.length === 0 && (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13 }}>
              No engagements scheduled for {MONTH_NAMES[month]} {year}.
            </div>
          )}
        </div>
      </Card>

      {/* Legend */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 12, padding: '0 4px' }}>
        {Object.entries(STATUS_COLORS).map(([status, color]) => (
          <div key={status} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: 'var(--text-secondary)' }}>
            <div style={{ width: 12, height: 12, borderRadius: 2, background: color }} />
            {status}
          </div>
        ))}
      </div>
    </div>
  );
}
